import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

function VideoTile({ label, stream, muted = false, info = '', className = '', videoClassName = '' }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.srcObject = stream || null;
  }, [stream]);

  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none ${className}`}>
      <div className={`flex-1 bg-slate-100 dark:bg-slate-950 ${videoClassName}`}>
        <video ref={videoRef} autoPlay playsInline muted={muted} className="h-full w-full object-cover" />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:text-slate-200">
        <span className="truncate font-semibold">{label}</span>
        <span className="shrink-0 text-slate-500 dark:text-slate-400">{info}</span>
      </div>
    </div>
  );
}

export default function VideoChatGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = normalizedState || gameState || {};

  const mySocketId = user?.socketId || socket?.id || user?.userId || null;
  const participants = useMemo(() => state?.participants || {}, [state?.participants]);

  const [isInCall, setIsInCall] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isCamEnabled, setIsCamEnabled] = useState(true);
  const [mediaError, setMediaError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [displayMode, setDisplayMode] = useState('grid');
  const [primarySize, setPrimarySize] = useState('balanced');
  const [featuredPeerId, setFeaturedPeerId] = useState('');
  const [audioLevels, setAudioLevels] = useState({});
  const [localStreamReady, setLocalStreamReady] = useState(false);

  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const audioContextRef = useRef(null);
  const analyzerRefs = useRef(new Map());
  const animationFrameRef = useRef(null);
  const pendingCandidatesRef = useRef({});
  const processedSignalIdsRef = useRef(new Set());

  const remoteInCallIds = useMemo(() => Object.values(participants)
    .filter((participant) => participant?.inCall && participant.socketId !== mySocketId)
    .map((participant) => participant.socketId), [participants, mySocketId]);

  const myParticipant = mySocketId ? participants[mySocketId] : null;
  const activeRemoteParticipants = useMemo(() => Object.values(participants)
    .filter((participant) => participant.inCall && participant.socketId !== mySocketId), [participants, mySocketId]);

  const featuredParticipantId = useMemo(() => {
    if (displayMode === 'grid') return null;
    if (displayMode === 'focus') {
      return featuredPeerId || activeRemoteParticipants[0]?.socketId || null;
    }
    if (displayMode === 'auto') {
      if (activeRemoteParticipants.length === 0) return null;
      return activeRemoteParticipants.reduce((loudest, participant) => {
        const level = audioLevels[participant.socketId] || 0;
        if (!loudest) return participant;
        return (audioLevels[loudest.socketId] || 0) >= level ? loudest : participant;
      }, null)?.socketId || null;
    }
    return null;
  }, [activeRemoteParticipants, audioLevels, displayMode, featuredPeerId]);

  const featuredParticipant = useMemo(() => activeRemoteParticipants.find((participant) => participant.socketId === featuredParticipantId) || null, [activeRemoteParticipants, featuredParticipantId]);
  const secondaryParticipants = useMemo(() => activeRemoteParticipants.filter((participant) => participant.socketId !== featuredParticipantId), [activeRemoteParticipants, featuredParticipantId]);

  const cleanupPeer = useCallback((peerId) => {
    const connection = peerConnectionsRef.current[peerId];
    if (connection) {
      try { connection.onicecandidate = null; } catch (error) {}
      try { connection.ontrack = null; } catch (error) {}
      try { connection.onconnectionstatechange = null; } catch (error) {}
      try { connection.close(); } catch (error) {}
      delete peerConnectionsRef.current[peerId];
    }

    setRemoteStreams((current) => {
      if (!current[peerId]) return current;
      const next = { ...current };
      delete next[peerId];
      return next;
    });
    delete pendingCandidatesRef.current[peerId];
  }, []);

  const cleanupAllPeers = useCallback(() => {
    Object.keys(peerConnectionsRef.current).forEach((peerId) => cleanupPeer(peerId));
  }, [cleanupPeer]);

  const applyPendingCandidates = useCallback(async (peerId) => {
    const connection = peerConnectionsRef.current[peerId];
    const pending = pendingCandidatesRef.current[peerId] || [];
    if (!connection || pending.length === 0 || !connection.remoteDescription) return;

    while (pending.length > 0) {
      const candidate = pending.shift();
      try {
        await connection.addIceCandidate(candidate);
      } catch (error) {
        // ignore malformed/late candidates
      }
    }
  }, []);

  const createPeerConnection = useCallback(async (peerId, shouldCreateOffer) => {
    if (!mySocketId) return null;
    if (peerConnectionsRef.current[peerId]) return peerConnectionsRef.current[peerId];

    const connection = new RTCPeerConnection(RTC_CONFIG);
    peerConnectionsRef.current[peerId] = connection;

    const localStream = localStreamRef.current;
    if (localStream) {
      localStream.getTracks().forEach((track) => {
        connection.addTrack(track, localStream);
      });
    }

    connection.onicecandidate = async (event) => {
      if (!event.candidate) return;
      await submitAction({
        type: 'signal',
        to: peerId,
        payload: { candidate: event.candidate },
      });
    };

    connection.ontrack = (event) => {
      const [stream] = event.streams || [];
      if (!stream) return;
      setRemoteStreams((current) => ({ ...current, [peerId]: stream }));
    };

    connection.onconnectionstatechange = () => {
      if (['failed', 'disconnected', 'closed'].includes(connection.connectionState)) {
        cleanupPeer(peerId);
      }
    };

    if (shouldCreateOffer) {
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);
      await submitAction({
        type: 'signal',
        to: peerId,
        payload: { description: connection.localDescription },
      });
    }

    return connection;
  }, [cleanupPeer, mySocketId, submitAction]);

  const processSignal = useCallback(async (signal) => {
    const { from, payload } = signal;
    if (!from || !payload) return;

    const connection = await createPeerConnection(from, false);
    if (!connection) return;

    if (payload.description) {
      const description = payload.description;
      await connection.setRemoteDescription(description);
      await applyPendingCandidates(from);

      if (description.type === 'offer') {
        const answer = await connection.createAnswer();
        await connection.setLocalDescription(answer);
        await submitAction({
          type: 'signal',
          to: from,
          payload: { description: connection.localDescription },
        });
      }
    }

    if (payload.candidate) {
      const candidate = new RTCIceCandidate(payload.candidate);
      if (connection.remoteDescription) {
        try {
          await connection.addIceCandidate(candidate);
        } catch (error) {
          // ignore stale candidate
        }
      } else {
        if (!pendingCandidatesRef.current[from]) pendingCandidatesRef.current[from] = [];
        pendingCandidatesRef.current[from].push(candidate);
      }
    }
  }, [applyPendingCandidates, createPeerConnection, submitAction]);

  const startLocalMedia = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    localStreamRef.current = stream;
    setLocalStreamReady(true);
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }
    return stream;
  }, []);

  const stopLocalMedia = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      try { track.stop(); } catch (error) {}
    });
    localStreamRef.current = null;
    setLocalStreamReady(false);
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
  }, []);

  const joinCall = useCallback(async () => {
    if (!mySocketId || isInCall || isConnecting) return;
    setMediaError('');
    setIsConnecting(true);

    try {
      await startLocalMedia();
      await submitAction({ type: 'joinCall', micEnabled: isMicEnabled, camEnabled: isCamEnabled });
      setIsInCall(true);

      for (const peerId of remoteInCallIds) {
        await createPeerConnection(peerId, mySocketId < peerId);
      }
    } catch (error) {
      setMediaError(error?.message || 'Unable to access camera/microphone.');
      stopLocalMedia();
      cleanupAllPeers();
      setIsInCall(false);
    } finally {
      setIsConnecting(false);
    }
  }, [cleanupAllPeers, createPeerConnection, isCamEnabled, isConnecting, isInCall, isMicEnabled, mySocketId, remoteInCallIds, startLocalMedia, stopLocalMedia, submitAction]);

  const leaveCall = useCallback(async () => {
    setIsInCall(false);
    cleanupAllPeers();
    stopLocalMedia();
    setRemoteStreams({});
    if (mySocketId) {
      await submitAction({ type: 'leaveCall' });
    }
  }, [cleanupAllPeers, mySocketId, stopLocalMedia, submitAction]);

  const toggleMic = useCallback(async () => {
    const next = !isMicEnabled;
    setIsMicEnabled(next);

    const stream = localStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = next;
      });
    }

    if (mySocketId) {
      await submitAction({ type: 'updateMedia', micEnabled: next, camEnabled: isCamEnabled });
    }
  }, [isCamEnabled, isMicEnabled, mySocketId, submitAction]);

  const toggleCam = useCallback(async () => {
    const next = !isCamEnabled;
    setIsCamEnabled(next);

    const stream = localStreamRef.current;
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = next;
      });
    }

    if (mySocketId) {
      await submitAction({ type: 'updateMedia', micEnabled: isMicEnabled, camEnabled: next });
    }
  }, [isCamEnabled, isMicEnabled, mySocketId, submitAction]);

  useEffect(() => {
    if (!localVideoRef.current) return;
    localVideoRef.current.srcObject = localStreamRef.current || null;
  }, [displayMode, isInCall, localStreamReady, primarySize]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }

    const context = audioContextRef.current;
    context.resume().catch(() => {});

    const streamEntries = [];
    if (localStreamReady && localStreamRef.current) {
      streamEntries.push({ id: 'local', stream: localStreamRef.current });
    }
    Object.entries(remoteStreams).forEach(([peerId, stream]) => {
      if (stream) streamEntries.push({ id: peerId, stream });
    });

    const activeIds = new Set(streamEntries.map(({ id }) => id));
    analyzerRefs.current.forEach((entry, id) => {
      if (!activeIds.has(id)) {
        try { entry.source.disconnect(); } catch (error) {}
        try { entry.analyser.disconnect(); } catch (error) {}
        analyzerRefs.current.delete(id);
      }
    });

    streamEntries.forEach(({ id, stream }) => {
      if (!stream || analyzerRefs.current.has(id)) return;
      try {
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyzerRefs.current.set(id, { source, analyser });
      } catch (error) {
        // ignore unsupported stream sources
      }
    });

    const measure = () => {
      const nextLevels = {};
      analyzerRefs.current.forEach((entry, id) => {
        const frequencyData = new Uint8Array(entry.analyser.frequencyBinCount);
        entry.analyser.getByteFrequencyData(frequencyData);
        const sum = frequencyData.reduce((total, value) => total + value, 0);
        nextLevels[id] = sum / frequencyData.length / 255;
      });
      setAudioLevels(nextLevels);
      animationFrameRef.current = requestAnimationFrame(measure);
    };

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(measure);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [localStreamReady, remoteStreams]);

  useEffect(() => {
    if (!mySocketId) return;
    const incomingSignals = Array.isArray(state?.signals)
      ? state.signals.filter((signal) => signal.to === mySocketId)
      : [];

    const unseenSignals = incomingSignals.filter((signal) => !processedSignalIdsRef.current.has(signal.id));
    if (unseenSignals.length === 0) return;

    let cancelled = false;
    const run = async () => {
      for (const signal of unseenSignals) {
        if (cancelled) return;
        try {
          await processSignal(signal);
          processedSignalIdsRef.current.add(signal.id);
        } catch (error) {
          // ignore single signal failures so the queue can continue
        }
      }

      const ids = unseenSignals.map((signal) => signal.id);
      if (ids.length > 0) {
        await submitAction({ type: 'ackSignals', signalIds: ids });
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [mySocketId, processSignal, state?.signals, submitAction]);

  useEffect(() => {
    if (!isInCall || !mySocketId) return;
    remoteInCallIds.forEach((peerId) => {
      if (!peerConnectionsRef.current[peerId]) {
        createPeerConnection(peerId, mySocketId < peerId);
      }
    });

    Object.keys(peerConnectionsRef.current)
      .filter((peerId) => !remoteInCallIds.includes(peerId))
      .forEach((peerId) => cleanupPeer(peerId));
  }, [cleanupPeer, createPeerConnection, isInCall, mySocketId, remoteInCallIds]);

  useEffect(() => {
    if (myParticipant) {
      setIsInCall(!!myParticipant.inCall);
      if (typeof myParticipant.micEnabled === 'boolean') setIsMicEnabled(!!myParticipant.micEnabled);
      if (typeof myParticipant.camEnabled === 'boolean') setIsCamEnabled(!!myParticipant.camEnabled);
    }
  }, [myParticipant]);

  useEffect(() => () => {
    cleanupAllPeers();
    stopLocalMedia();
  }, [cleanupAllPeers, stopLocalMedia]);

  const callParticipants = Object.values(participants).filter((participant) => participant.inCall);
  const featuredTileSizeClass = primarySize === 'expanded' ? 'min-h-[320px] lg:min-h-[460px]' : primarySize === 'compact' ? 'min-h-[220px] lg:min-h-[280px]' : 'min-h-[260px] lg:min-h-[360px]';
  const featuredVideoSizeClass = primarySize === 'expanded' ? 'min-h-[320px] lg:min-h-[460px]' : primarySize === 'compact' ? 'min-h-[220px] lg:min-h-[280px]' : 'min-h-[260px] lg:min-h-[360px]';
  const secondaryTileSizeClass = primarySize === 'expanded' ? 'min-h-[180px]' : primarySize === 'compact' ? 'min-h-[150px]' : 'min-h-[160px]';

  return (
    <div className="mx-auto w-full max-w-6xl p-3 text-slate-900 dark:text-slate-100">
      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-950/90 dark:shadow-[0_24px_70px_rgba(2,6,23,0.45)]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Video Chat</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Join the room call and chat live with everyone in this lobby.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-full bg-slate-100 p-1 dark:bg-slate-800">
              <button type="button" onClick={() => setDisplayMode('grid')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${displayMode === 'grid' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Grid</button>
              <button type="button" onClick={() => setDisplayMode('focus')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${displayMode === 'focus' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Focus</button>
              <button type="button" onClick={() => setDisplayMode('auto')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${displayMode === 'auto' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Loudest</button>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-slate-100 p-1 dark:bg-slate-800">
              <button type="button" onClick={() => setPrimarySize('compact')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${primarySize === 'compact' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Compact</button>
              <button type="button" onClick={() => setPrimarySize('balanced')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${primarySize === 'balanced' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Balanced</button>
              <button type="button" onClick={() => setPrimarySize('expanded')} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${primarySize === 'expanded' ? 'bg-white text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>Expanded</button>
            </div>
            {displayMode !== 'grid' ? (
              <select
                value={featuredPeerId}
                onChange={(event) => setFeaturedPeerId(event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="">Auto pick</option>
                {activeRemoteParticipants.map((participant) => (
                  <option key={participant.socketId} value={participant.socketId}>{participant.username || participant.socketId}</option>
                ))}
              </select>
            ) : null}
            {!isInCall ? (
              <button
                type="button"
                onClick={joinCall}
                disabled={isConnecting}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isConnecting ? 'Joining...' : 'Join Call'}
              </button>
            ) : (
              <button
                type="button"
                onClick={leaveCall}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-500"
              >
                Leave Call
              </button>
            )}
            <button
              type="button"
              onClick={toggleMic}
              disabled={!isInCall}
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${isMicEnabled ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {isMicEnabled ? 'Mic On' : 'Mic Off'}
            </button>
            <button
              type="button"
              onClick={toggleCam}
              disabled={!isInCall}
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${isCamEnabled ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {isCamEnabled ? 'Cam On' : 'Cam Off'}
            </button>
          </div>
        </div>

        {mediaError ? (
          <div className="mb-3 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-500/60 dark:bg-rose-950/40 dark:text-rose-100">
            {mediaError}
          </div>
        ) : null}

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
          In call: {callParticipants.length} / {(state?.players || gamePlayers || []).length}
        </div>

        {displayMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
              <div className="aspect-video w-full bg-slate-100 dark:bg-slate-950">
                <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:text-slate-200">
                <span className="truncate font-semibold">{user?.username || mySocketId || 'You'}</span>
                <span className="shrink-0 text-slate-500 dark:text-slate-400">{isInCall ? 'You' : 'Not in call'}</span>
              </div>
            </div>

            {activeRemoteParticipants.map((participant) => (
              <VideoTile
                key={participant.socketId}
                label={participant.username || participant.socketId}
                stream={remoteStreams[participant.socketId] || null}
                info={`${participant.micEnabled ? 'Mic' : 'Muted'} • ${participant.camEnabled ? 'Cam' : 'No Cam'}`}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 lg:grid-cols-[1.7fr_1fr]">
              <div className="min-h-[240px]">
                {featuredParticipant ? (
                  <VideoTile
                    label={featuredParticipant.username || featuredParticipant.socketId}
                    stream={remoteStreams[featuredParticipant.socketId] || null}
                    info={`${featuredParticipant.micEnabled ? 'Mic' : 'Muted'} • ${featuredParticipant.camEnabled ? 'Cam' : 'No Cam'}`}
                    className={`${featuredTileSizeClass} h-full`}
                    videoClassName={featuredVideoSizeClass}
                  />
                ) : (
                  <div className="flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-100 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                    Waiting for a participant to join.
                  </div>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
                  <div className="aspect-video w-full bg-slate-100 dark:bg-slate-950">
                    <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:text-slate-200">
                    <span className="truncate font-semibold">{user?.username || mySocketId || 'You'}</span>
                    <span className="shrink-0 text-slate-500 dark:text-slate-400">{isInCall ? 'You' : 'Not in call'}</span>
                  </div>
                </div>
                {secondaryParticipants.map((participant) => (
                  <VideoTile
                    key={participant.socketId}
                    label={participant.username || participant.socketId}
                    stream={remoteStreams[participant.socketId] || null}
                    info={`${participant.micEnabled ? 'Mic' : 'Muted'} • ${participant.camEnabled ? 'Cam' : 'No Cam'}`}
                    className={`${secondaryTileSizeClass}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {!isInCall ? (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
            Press Join Call to request camera and microphone access.
          </div>
        ) : null}
      </div>
    </div>
  );
}

VideoChatGame.Guide = function VideoChatGuide() {
  return (
    <div className="text-sm text-slate-800 dark:text-slate-200">
      Join the call to share your camera and microphone with other players in the room. Toggle mic and camera while connected.
    </div>
  );
};
